import React, { useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Skeleton,
  Alert,
  IconButton,
  Button,
  Stack,
  Divider,
  Paper,
} from '@mui/material';
import {
  Refresh,
  ArrowForward,
  Add,
  Print as PrintIcon,
  PersonAdd,
  Inventory2Outlined,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { useProducts } from '../hooks/useProducts';
import { useCurrentUser } from '../auth';
import { ORGANIZATION_LIST_USERS_PERMISSION } from '../constants/permissions';
import PageHeader from '../components/PageHeader';
import SectionCard from '../components/SectionCard';
import { ProductLanguageChips } from '../components/products/ProductLanguageChips';

const RECENT_PRODUCTS_LIMIT = 8;

const ONBOARDING_STEPS = [
  {
    icon: <Inventory2Outlined sx={{ fontSize: 28, color: 'primary.dark' }} />,
    titleKey: 'dashboard.onboarding.step1Title',
    descKey: 'dashboard.onboarding.step1Desc',
  },
  {
    icon: <TranslateIcon sx={{ fontSize: 28, color: 'primary.dark' }} />,
    titleKey: 'dashboard.onboarding.step2Title',
    descKey: 'dashboard.onboarding.step2Desc',
  },
  {
    icon: <PrintIcon sx={{ fontSize: 28, color: 'primary.dark' }} />,
    titleKey: 'dashboard.onboarding.step3Title',
    descKey: 'dashboard.onboarding.step3Desc',
  },
];

const formatDate = (value: string | undefined, locale: string) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
};

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { permissions } = useCurrentUser();
  const { stats, loading: statsLoading, error: statsError, refetch: refetchStats } = useDashboardStats();
  const { products, loading: productsLoading } = useProducts();

  const canManageUsers = permissions.includes(ORGANIZATION_LIST_USERS_PERMISSION);

  const recentProducts = products.slice(0, RECENT_PRODUCTS_LIMIT);
  const isFirstTime = !productsLoading && recentProducts.length === 0;
  const hasSampleOnly = !productsLoading && recentProducts.length === 1;

  const handleProductOpen = useCallback(
    (productId: string) => {
      navigate(`/products/${productId}`);
    },
    [navigate]
  );

  const quickActions = [
    { label: t('products.newProduct'), icon: <Add fontSize="small" />, path: '/products/new' },
    { label: t('printing.title'), icon: <PrintIcon fontSize="small" />, path: '/printing' },
    ...(canManageUsers
      ? [{ label: t('organizationUsers.addUser'), icon: <PersonAdd fontSize="small" />, path: '/users' }]
      : []),
  ];

  if (statsError && isFirstTime) {
    return (
      <Box>
        <PageHeader title={t('nav.dashboard')} subtitle={t('dashboard.subtitle')} />
        <Alert
          severity="error"
          action={
            <IconButton onClick={refetchStats} size="small">
              <Refresh />
            </IconButton>
          }
        >
          {statsError}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={t('nav.dashboard')}
        subtitle={t('dashboard.subtitle')}
        actions={
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/products/new')}>
            {t('products.newProduct')}
          </Button>
        }
      />

      {/* Onboarding state: no products yet */}
      {isFirstTime && (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'primary.light',
            backgroundColor: '#f6fbf6',
            p: { xs: 3, md: 5 },
            mb: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            {t('dashboard.onboarding.title')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4, maxWidth: 480, mx: 'auto' }}>
            {t('dashboard.onboarding.subtitle')}
          </Typography>

          <Grid container spacing={3} justifyContent="center" sx={{ mb: 4 }}>
            {ONBOARDING_STEPS.map((step, idx) => (
              <Grid item xs={12} sm={4} key={idx}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: 'rgba(63, 156, 70, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 1.5,
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ mb: 0.5, color: 'text.primary' }}
                  >
                    {t(step.titleKey)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(step.descKey)}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Button
            variant="contained"
            size="large"
            startIcon={<Add />}
            onClick={() => navigate('/products/new')}
            sx={{ minWidth: 220 }}
          >
            {t('dashboard.onboarding.cta')}
          </Button>
        </Paper>
      )}

      {/* New account banner: sample product exists but no real products yet */}
      {hasSampleOnly && (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'primary.light',
            backgroundColor: '#f6fbf6',
            px: 3,
            py: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.25 }}>
              {t('dashboard.onboarding.bannerTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.onboarding.bannerSubtitle')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => navigate('/products/new')}
            sx={{ flexShrink: 0 }}
          >
            {t('dashboard.onboarding.cta')}
          </Button>
        </Paper>
      )}

      {/* Recent Products */}
      {!isFirstTime && (
        <SectionCard sx={{ mb: 3 }} padded={false}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              {t('dashboard.recentProducts')}
            </Typography>
            <Button
              size="small"
              endIcon={<ArrowForward fontSize="small" />}
              onClick={() => navigate('/products')}
              sx={{ fontWeight: 600, color: 'primary.dark' }}
            >
              {t('dashboard.viewAllProducts')}
            </Button>
          </Box>

          {productsLoading ? (
            <Stack divider={<Divider />} sx={{ px: 3 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Box key={i} sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Skeleton variant="text" width="30%" />
                  <Skeleton variant="text" width="15%" />
                  <Skeleton variant="text" width="20%" sx={{ ml: 'auto' }} />
                </Box>
              ))}
            </Stack>
          ) : (
            <Stack divider={<Divider />}>
              {recentProducts.map((product) => {
                const updated = formatDate(product.updated_at, i18n.language);
                return (
                  <Box
                    key={product.id}
                    tabIndex={0}
                    role="button"
                    onClick={() => handleProductOpen(product.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleProductOpen(product.id);
                    }}
                    sx={{
                      px: 3,
                      py: 1.75,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      cursor: 'pointer',
                      transition: 'background-color 0.14s ease',
                      '&:hover': { bgcolor: 'rgba(63, 156, 70, 0.04)' },
                      '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: -2,
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        flex: '1 1 0',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {product.name}
                    </Typography>

                    <Box sx={{ flex: '0 0 auto', display: { xs: 'none', sm: 'block' } }}>
                      <ProductLanguageChips
                        languages={product.translation_languages}
                        baseLanguage={product.base_language}
                        maxVisible={5}
                      />
                    </Box>

                    {updated && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ flex: '0 0 auto', display: { xs: 'none', md: 'block' }, minWidth: 100, textAlign: 'right' }}
                      >
                        {updated}
                      </Typography>
                    )}

                    <ArrowForward sx={{ fontSize: 16, color: 'text.disabled', flex: '0 0 auto' }} />
                  </Box>
                );
              })}
            </Stack>
          )}
        </SectionCard>
      )}

      {/* Quick Actions + Stats */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <SectionCard>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              {t('dashboard.quickActions')}
            </Typography>
            <Stack divider={<Divider flexItem />}>
              {quickActions.map((action) => (
                <Button
                  key={action.path}
                  variant="text"
                  onClick={() => navigate(action.path)}
                  endIcon={<ArrowForward fontSize="small" />}
                  sx={{
                    justifyContent: 'space-between',
                    py: 1.25,
                    px: 0,
                    color: 'text.primary',
                    fontWeight: 600,
                    '&:hover': { backgroundColor: 'transparent', color: 'primary.dark' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 26,
                        height: 26,
                        borderRadius: 0.75,
                        color: 'primary.dark',
                        backgroundColor: 'rgba(63, 156, 70, 0.12)',
                      }}
                    >
                      {action.icon}
                    </Box>
                    <Typography variant="body2" fontWeight={600}>
                      {action.label}
                    </Typography>
                  </Box>
                </Button>
              ))}
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <SectionCard>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              {t('dashboard.stats.productsTitle')} / {t('dashboard.stats.translationsTitle')} /{' '}
              {t('dashboard.stats.languagesTitle')}
            </Typography>

            {statsLoading ? (
              <Stack spacing={2}>
                <Skeleton variant="text" width="50%" />
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="60%" />
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1 }}>
                    {stats?.total_products?.toString() || '0'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.stats.productsSubtitle')}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                    {stats?.available_translations?.toString() || '0'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.stats.translationsSubtitle')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                    {stats?.total_languages?.toString() || '0'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.stats.languagesSubtitle')}
                  </Typography>
                </Box>
              </Stack>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
